import { ChangeEvent, useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Grid, { GridBaseProps } from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getPricebookPeriodOverview } from "../services";
import { PricebookPeriodOverviewRow } from "../types/price-book.type";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import { useDebounce } from "../utils/hooks/useDebounce";
import { notifyPricePeriodsChanged } from "../utils/hooks/useExpiringPricePeriodCount";
import FilterField from "../components/Product/shared/FilterField";
import { useSearchablePagedRows } from "../components/Product/shared/useSearchablePagedRows";
import {
  ALL_FILTER_VALUE,
  PRICE_PERIOD_STATUS_PARAM,
  priceBookPeriodsPath,
} from "../components/Product/shared/constants";
import PricePeriodTable from "../components/Product/PricePeriod/Table";
import ExtendPeriodDialog, {
  ExtendPeriodTarget,
} from "../components/Product/PriceBook/Periods/ExtendPeriodDialog";
import {
  EXPIRING_DAYS_ERROR,
  EXPIRING_WINDOW_DAYS,
  PERIOD_STATUSES,
  PERIOD_STATUS_LABEL,
  parseExpiringDays,
  toPeriodStatus,
} from "../components/Product/PriceBook/Periods/periodModel";

const STATUS_SIZE: GridBaseProps["size"] = { xs: 12, sm: 6, lg: 3 };
const DAYS_SIZE: GridBaseProps["size"] = { xs: 12, sm: 6, lg: 4 };
const DAYS_DEBOUNCE_MS = 400;

/**
 * ราคาตามช่วงเวลา: every live book's price periods — ใกล้หมด / กำลังใช้ / ล่วงหน้า / หมดแล้ว —
 * with the price each line goes back to once its period ends. A row opens its book on the
 * ช่วงราคา tab; a catalogue writer can extend a period from here.
 */
export default function PricePeriodPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = useUserRole();
  const canWrite = featureAccess.productWrite(role);

  // The list banner and the menu badge link here on the expiring periods.
  const [statusFilter, setStatusFilter] = useState<string>(
    () => toPeriodStatus(searchParams.get(PRICE_PERIOD_STATUS_PARAM)) ?? ALL_FILTER_VALUE,
  );
  const [daysText, setDaysText] = useState<string>(
    String(EXPIRING_WINDOW_DAYS.DEFAULT),
  );
  const [appliedDays, setAppliedDays] = useState<number>(
    EXPIRING_WINDOW_DAYS.DEFAULT,
  );
  const [extendTarget, setExtendTarget] = useState<ExtendPeriodTarget | null>(null);

  const debouncedDaysText = useDebounce(daysText, DAYS_DEBOUNCE_MS);
  const isDaysValid = parseExpiringDays(daysText) !== null;
  const daysHelperText = isDaysValid ? undefined : EXPIRING_DAYS_ERROR;

  const fetchPeriods = useCallback(
    async (page: number, limit: number) => {
      const {
        data: { data },
      } = await getPricebookPeriodOverview(page, limit, {
        status: toPeriodStatus(statusFilter),
        days: appliedDays,
      });
      return data ?? { rows: [], count: 0 };
    },
    [statusFilter, appliedDays],
  );

  const { page, setPage, limit, setLimit, rows, totalCount, loading, reload } =
    useSearchablePagedRows<PricebookPeriodOverviewRow>({
      open: true,
      fetchRows: fetchPeriods,
    });

  // Only a window the API accepts is applied; a half-typed one keeps the list as it is.
  const typedDays = parseExpiringDays(debouncedDaysText);
  if (typedDays !== null && typedDays !== appliedDays) {
    setAppliedDays(typedDays);
    setPage(1);
  }

  const handleChangeStatus = (event: ChangeEvent<HTMLInputElement>) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleChangeDays = (event: ChangeEvent<HTMLInputElement>) => {
    setDaysText(event.target.value);
  };

  const handleOpen = (row: PricebookPeriodOverviewRow) =>
    navigate(priceBookPeriodsPath(row.pricebook_uuid));

  const handleExtend = (row: PricebookPeriodOverviewRow) =>
    setExtendTarget({ pricebookUuid: row.pricebook_uuid, period: row });
  const handleCloseExtend = () => setExtendTarget(null);
  const handleExtended = () => {
    setExtendTarget(null);
    reload();
    notifyPricePeriodsChanged();
  };

  return (
    <Stack sx={{ py: 1 }}>
      <Stack gap={0.5} mb={1}>
        <Typography variant="h6">ราคาตามช่วงเวลา</Typography>
        <Typography variant="body2" color="text.secondary">
          ช่วงราคาพิเศษของทุกสมุด หมดช่วงแล้วสินค้ากลับไปขายที่ราคาถัดไป · กดแถวเพื่อเปิดสมุดราคา
        </Typography>
      </Stack>
      <Paper sx={{ padding: "24px 16px 8px", mb: 2 }}>
        <Grid container spacing={2} mb={2}>
          <FilterField label="สถานะ" size={STATUS_SIZE}>
            <TextField
              select
              size="small"
              fullWidth
              value={statusFilter}
              onChange={handleChangeStatus}
            >
              <MenuItem value={ALL_FILTER_VALUE}>ทั้งหมด</MenuItem>
              {PERIOD_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {PERIOD_STATUS_LABEL[status]}
                </MenuItem>
              ))}
            </TextField>
          </FilterField>
          <FilterField label="ใกล้หมดภายใน (วัน)" size={DAYS_SIZE}>
            <TextField
              size="small"
              fullWidth
              value={daysText}
              onChange={handleChangeDays}
              error={!isDaysValid}
              helperText={daysHelperText}
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
            />
          </FilterField>
        </Grid>
      </Paper>
      <PricePeriodTable
        rows={rows}
        loading={loading}
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        canExtend={canWrite}
        onOpen={handleOpen}
        onExtend={handleExtend}
      />
      <ExtendPeriodDialog
        target={extendTarget}
        onClose={handleCloseExtend}
        onExtended={handleExtended}
      />
    </Stack>
  );
}
